// Звуки: синтез через Web Audio (без файлов — работает офлайн). Включаются после первого касания.
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;
try {
  muted = localStorage.getItem('krutagidon-muted') === '1';
} catch {
  /* ignore */
}

interface AudioWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

function ensure(): AudioContext | null {
  if (ctx) return ctx;
  const AC = window.AudioContext ?? (window as AudioWindow).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = 0.5;
  master.connect(ctx.destination);
  return ctx;
}

/**
 * Вызывать только из обработчика настоящего жеста (click/pointerup/keydown/touchend):
 * иначе браузер ругается «AudioContext was not allowed to start».
 */
export function unlock(): void {
  if (muted && !ctx) return;
  const c = ensure();
  if (c && c.state === 'suspended') c.resume().catch(() => {});
}
export function isMuted(): boolean {
  return muted;
}
export function setMuted(m: boolean): void {
  muted = m;
  try {
    localStorage.setItem('krutagidon-muted', muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** Активный контекст (вызывается только когда он есть — см. sfx) */
function live(): { ctx: AudioContext; master: GainNode } {
  if (!ctx || !master) throw new Error('AudioContext не создан');
  return { ctx, master };
}

function env(g: GainNode, t0: number, a: number, d: number, peak = 1, sustain = 0): void {
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(peak, t0 + a);
  g.gain.exponentialRampToValueAtTime(Math.max(0.0001, sustain), t0 + a + d);
}

interface ToneOpts {
  type?: OscillatorType;
  f0?: number;
  f1?: number | null;
  a?: number;
  d?: number;
  vol?: number;
  delay?: number;
}
function tone({ type = 'sine', f0 = 440, f1 = null, a = 0.005, d = 0.2, vol = 0.4, delay = 0 }: ToneOpts): void {
  const { ctx: c, master: m } = live();
  const o = c.createOscillator();
  const g = c.createGain();
  o.type = type;
  const t0 = c.currentTime + delay;
  o.frequency.setValueAtTime(f0, t0);
  if (f1) o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t0 + a + d);
  env(g, t0, a, d, vol);
  o.connect(g).connect(m);
  o.start(t0);
  o.stop(t0 + a + d + 0.05);
}

interface NoiseOpts {
  d?: number;
  vol?: number;
  f0?: number;
  f1?: number | null;
  q?: number;
  type?: BiquadFilterType;
  delay?: number;
  a?: number;
}
let noiseBuf: AudioBuffer | null = null;
function noise({ d = 0.15, vol = 0.3, f0 = 1500, f1 = null, q = 1, type = 'bandpass', delay = 0, a = 0.005 }: NoiseOpts): void {
  const { ctx: c, master: m } = live();
  if (!noiseBuf) {
    noiseBuf = c.createBuffer(1, c.sampleRate, c.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  }
  const s = c.createBufferSource();
  s.buffer = noiseBuf;
  const f = c.createBiquadFilter();
  f.type = type;
  const t0 = c.currentTime + delay;
  f.frequency.setValueAtTime(f0, t0);
  if (f1) f.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + a + d);
  f.Q.value = q;
  const g = c.createGain();
  env(g, t0, a, d, vol);
  s.connect(f).connect(g).connect(m);
  s.start(t0);
  s.stop(t0 + a + d + 0.05);
}

const SOUNDS = {
  play(): void { noise({ d: 0.12, vol: 0.25, f0: 3000, f1: 800 }); tone({ type: 'triangle', f0: 520, f1: 380, d: 0.12, vol: 0.12 }); },
  draw(): void { noise({ d: 0.06, vol: 0.18, f0: 4000, f1: 1500, a: 0.002 }); },
  select(): void { tone({ type: 'sine', f0: 900, d: 0.05, vol: 0.12 }); },
  buy(): void { tone({ f0: 1046, d: 0.12, vol: 0.25 }); tone({ f0: 1568, d: 0.25, vol: 0.25, delay: 0.07 }); },
  power(): void { tone({ type: 'triangle', f0: 660, d: 0.18, vol: 0.2 }); tone({ type: 'triangle', f0: 990, d: 0.25, vol: 0.15, delay: 0.05 }); },
  chips(): void { tone({ type: 'square', f0: 1800, d: 0.04, vol: 0.08 }); tone({ type: 'square', f0: 2400, d: 0.05, vol: 0.06, delay: 0.05 }); },
  damage(): void { tone({ type: 'sine', f0: 160, f1: 40, d: 0.25, vol: 0.6 }); noise({ d: 0.12, vol: 0.35, f0: 600, f1: 100, type: 'lowpass' }); },
  heal(): void { [523, 659, 784].forEach((f, i) => tone({ type: 'sine', f0: f, d: 0.25, vol: 0.15, delay: i * 0.07 })); },
  die(): void { tone({ type: 'sawtooth', f0: 90, f1: 20, d: 0.7, vol: 0.5 }); noise({ d: 0.5, vol: 0.4, f0: 400, f1: 60, type: 'lowpass' }); },
  defend(): void { tone({ type: 'square', f0: 1400, f1: 900, d: 0.18, vol: 0.12 }); noise({ d: 0.2, vol: 0.2, f0: 5000, q: 6 }); },
  attack(): void { noise({ d: 0.28, vol: 0.35, f0: 300, f1: 2500, q: 2 }); },
  turn(): void { tone({ f0: 880, d: 0.5, vol: 0.2 }); tone({ f0: 1760, d: 0.4, vol: 0.08 }); },
  bespredel(): void {
    for (let i = 0; i < 3; i++) {
      tone({ type: 'square', f0: 420, d: 0.12, vol: 0.12, delay: i * 0.26 });
      tone({ type: 'square', f0: 620, d: 0.12, vol: 0.12, delay: i * 0.26 + 0.13 });
    }
  },
  ddk(): void { tone({ type: 'sawtooth', f0: 220, f1: 110, d: 0.4, vol: 0.2 }); tone({ type: 'sine', f0: 110, d: 0.5, vol: 0.2, delay: 0.1 }); },
  loser(): void { [440, 415, 392, 370].forEach((f, i) => tone({ type: 'triangle', f0: f, d: 0.2, vol: 0.15, delay: i * 0.15 })); },
  prize(): void { [523, 659, 784, 1046].forEach((f, i) => tone({ f0: f, d: 0.3, vol: 0.18, delay: i * 0.09 })); },
  win(): void { [523, 659, 784, 1046, 784, 1046].forEach((f, i) => tone({ type: 'triangle', f0: f, d: 0.35, vol: 0.2, delay: i * 0.13 })); },
  error(): void { tone({ type: 'square', f0: 200, f1: 120, d: 0.15, vol: 0.12 }); },
  activate(): void { tone({ type: 'sine', f0: 700, f1: 1400, d: 0.2, vol: 0.15 }); },
  shuffle(): void { for (let i = 0; i < 4; i++) noise({ d: 0.05, vol: 0.15, f0: 3500, f1: 1200, delay: i * 0.06 }); },
  /** ачивка: восходящее арпеджио с искрой сверху */
  ach(): void {
    [784, 988, 1175, 1568].forEach((f, i) => tone({ type: 'triangle', f0: f, d: 0.22, vol: 0.16, delay: i * 0.075 }));
    tone({ type: 'sine', f0: 2349, d: 0.6, vol: 0.1, delay: 0.32 });
    noise({ d: 0.25, vol: 0.08, f0: 7000, q: 4, delay: 0.3 });
  },
  /** колокольчик: приглашение, новость */
  bell(): void { tone({ type: 'sine', f0: 1318, d: 0.35, vol: 0.18 }); tone({ type: 'sine', f0: 1760, d: 0.5, vol: 0.14, delay: 0.16 }); },
};

export type SoundName = keyof typeof SOUNDS;

export function sfx(name: SoundName): void {
  if (muted || !ctx || ctx.state !== 'running') return; // контекст создаётся только по жесту (unlock)
  try {
    SOUNDS[name]();
  } catch {
    /* звук — не критично */
  }
}
